/**
 * transferRecognizerRegistry.js
 *
 * Module-level singleton registry for TF.js speech-commands transfer recognizers.
 *
 * Why module-level?
 *   The transfer recognizer must accumulate examples across multiple React renders
 *   (user opens AudioModel for Class_1, records, closes, opens for Class_2, records…).
 *   React state is destroyed on component unmount, so we persist the recognizer
 *   instances here instead.
 *
 * One base recognizer is shared across all models (loading it is expensive ~2s).
 * Each model gets its own transfer recognizer keyed by modelId.
 */

import * as speechCommands from '@tensorflow-models/speech-commands'

let baseRecognizer = null
const transferMap = {} // modelId (string) → TransferSpeechCommandRecognizer
let transferSeq = 0 // monotonic suffix so transfer-model names never collide

/**
 * Returns the transfer recognizer for the given modelId.
 * Creates the base BROWSER_FFT recognizer once, then creates a transfer
 * recognizer per model on first access.
 *
 * @param {string|number} modelId
 * @returns {Promise<TransferSpeechCommandRecognizer>}
 */
export const getTransferRecognizer = async (modelId) => {
    const key = String(modelId)

    if (!baseRecognizer) {
        baseRecognizer = speechCommands.create('BROWSER_FFT')
        await baseRecognizer.ensureModelLoaded()
        console.log('[TransferRegistry] Base BROWSER_FFT recognizer loaded.')
    }

    if (!transferMap[key]) {
        transferMap[key] = baseRecognizer.createTransfer(`transfer-${key}`)
        console.log(`[TransferRegistry] Created transfer recognizer for model ${key}`)
    }

    return transferMap[key]
}

/**
 * Build a FRESH transfer recognizer for training. The speech-commands library
 * keeps an internal registry of transfer-model names, so createTransfer() throws
 * "already exists" if the same name is reused — and a cached recognizer keeps its
 * old classification head, which crashes when the class count changes. So each
 * train gets a uniquely-named, brand-new recognizer (examples are re-injected
 * from storage by the caller, so nothing is lost).
 *
 * @param {string|number} modelId
 * @returns {Promise<TransferSpeechCommandRecognizer>}
 */
export const freshTransferRecognizer = async (modelId) => {
    const key = String(modelId)
    if (!baseRecognizer) {
        baseRecognizer = speechCommands.create('BROWSER_FFT')
        await baseRecognizer.ensureModelLoaded()
    }
    transferSeq += 1
    transferMap[key] = baseRecognizer.createTransfer(
        `transfer-${key}-${transferSeq}`
    )
    return transferMap[key]
}

/**
 * Removes the transfer recognizer for the given modelId from memory.
 * Call this when the model is permanently deleted.
 *
 * @param {string|number} modelId
 */
export const clearTransferRecognizer = (modelId) => {
    const key = String(modelId)
    if (transferMap[key]) {
        delete transferMap[key]
        console.log(`[TransferRegistry] Cleared transfer recognizer for model ${key}`)
    }
}

